/**
 * Cockpit visual editor — pure model layer (no React, no JSDOM).
 *
 * This is the testable core of the Cockpit visual authoring experience. It owns:
 *
 *   1. The form-field DESCRIPTORS derived from each catalog component's Zod
 *      `propsSchema` — via Zod v4's `z.toJSONSchema()` (a stable, documented
 *      serialization) walked into a small field tree the props form renders.
 *   2. Default prop-bag synthesis for a freshly-inserted node (golden fixture
 *      first, schema defaults as the fallback).
 *   3. Immutable SPEC MUTATIONS the editor dispatches: insert / update-props /
 *      move / delete a node, and add / rename / reorder / delete a page, plus
 *      declared binding/action id management. Every mutation returns a NEW spec
 *      (structural sharing not required — specs are small, ≤400 nodes) so React
 *      state updates are clean and `parseCockpitSpec` can re-validate the result.
 *
 * Why a pure model: the rest of the web package unit-tests pure helpers with
 * vitest (no @testing-library). Keeping the mutation + field-derivation logic
 * here lets us prove "the editor builds a valid spec" / "multi-page add+switch
 * works" without mounting the React tree. The thin React components in
 * `cockpit-spec-editor.tsx` just dispatch these mutations + render these fields.
 */

import { z } from 'zod';
import {
  COCKPIT_LIBRARY,
  COCKPIT_ROOT_SLUG,
  COCKPIT_SPEC_VERSION,
  COCKPIT_SPEC_MAX_PAGES,
  getCockpitComponent,
  type CockpitSpec,
  type CockpitNode,
  type CockpitPage,
  type CockpitComponentCategory,
} from '@argusai/shared';

/* ─── Catalog grouping (the palette source of truth) ────────────────────── */

/** The category display order + labels for the palette accordion. */
export const COCKPIT_CATEGORY_ORDER: ReadonlyArray<{
  category: CockpitComponentCategory;
  label: string;
}> = [
  { category: 'layout', label: 'Layout' },
  { category: 'display', label: 'Display' },
  { category: 'data', label: 'Data' },
  { category: 'input', label: 'Input' },
  { category: 'action', label: 'Action' },
  { category: 'live', label: 'Live' },
];

export interface PaletteEntry {
  slug: string;
  label: string;
  description: string;
  category: CockpitComponentCategory;
  /** True when this component carries at least one binding slot. */
  hasBindings: boolean;
  /** True when this component carries an action slot (top-level or nested). */
  hasActions: boolean;
}

/**
 * The palette grouped by category. EXCLUDES the root `CockpitContainer` (it is
 * only ever a page root, never a hand-inserted child) so the picker can't create
 * an illegal nested container that the schema would reject.
 */
export function getPaletteGroups(): Array<{
  category: CockpitComponentCategory;
  label: string;
  entries: PaletteEntry[];
}> {
  const byCat = new Map<CockpitComponentCategory, PaletteEntry[]>();
  for (const contract of Object.values(COCKPIT_LIBRARY)) {
    if (contract.slug === COCKPIT_ROOT_SLUG) continue;
    const entry: PaletteEntry = {
      slug: contract.slug,
      label: contract.label,
      description: contract.description,
      category: contract.category,
      hasBindings: contract.bindingSlots.length > 0,
      hasActions:
        contract.actionSlots.length > 0 ||
        // `nestedActionPaths` is optional on the contract (some published
        // @argusai/shared type-builds predate the field); read it defensively
        // so the palette flags ButtonGroup/InlineActions as action-carrying.
        (((contract as { nestedActionPaths?: string[] }).nestedActionPaths
          ?.length ?? 0) > 0),
    };
    const arr = byCat.get(contract.category) ?? [];
    arr.push(entry);
    byCat.set(contract.category, arr);
  }
  return COCKPIT_CATEGORY_ORDER.map(({ category, label }) => ({
    category,
    label,
    entries: (byCat.get(category) ?? []).sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
  })).filter((g) => g.entries.length > 0);
}

/* ─── JSON-Schema → field descriptors ───────────────────────────────────── */

export type FieldKind =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'string-or-number'
  | 'object'
  | 'array'
  | 'tuple'
  | 'binding'
  | 'action'
  | 'unknown';

export interface FieldDescriptor {
  /** The prop key (or array index label for tuple/array item descriptors). */
  key: string;
  /** Human label (prettified from the key). */
  label: string;
  kind: FieldKind;
  required: boolean;
  /** enum: the allowed string values. */
  enumValues?: string[];
  /** string/number bounds (for input attributes + inline hints). */
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  maxItems?: number;
  /** default value declared on the schema. */
  defaultValue?: unknown;
  /** object: its child field descriptors. */
  fields?: FieldDescriptor[];
  /** array: the descriptor for each item. */
  item?: FieldDescriptor;
  /** tuple: the per-position descriptors. */
  prefixItems?: FieldDescriptor[];
}

type JsonSchema = Record<string, unknown>;

/** Prettify a camelCase / snake_case key into a Title Case label. */
export function prettyLabel(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Convert ONE component's Zod propsSchema into an ordered list of top-level
 * field descriptors the props form renders. Binding/action slot props are
 * re-tagged (`binding` / `action`) so the form renders them as a ref picker.
 */
export function deriveFields(slug: string): FieldDescriptor[] {
  const contract = getCockpitComponent(slug);
  if (!contract) return [];
  let json: JsonSchema;
  try {
    json = z.toJSONSchema(contract.propsSchema as z.ZodTypeAny, {
      unrepresentable: 'any',
      io: 'input',
    }) as JsonSchema;
  } catch {
    return [];
  }
  const bindingProps = new Set(contract.bindingSlots.map((s) => s.prop));
  const actionProps = new Set(contract.actionSlots.map((s) => s.prop));
  const props = (json.properties ?? {}) as Record<string, JsonSchema>;
  const required = new Set(
    Array.isArray(json.required) ? (json.required as string[]) : [],
  );
  return Object.keys(props).map((key) => {
    const base = fieldFromJson(key, props[key]!, required.has(key));
    if (bindingProps.has(key)) return { ...base, kind: 'binding' as const };
    if (actionProps.has(key)) return { ...base, kind: 'action' as const };
    return base;
  });
}

function fieldFromJson(
  key: string,
  schema: JsonSchema,
  required: boolean,
): FieldDescriptor {
  const label = prettyLabel(key);
  const base: FieldDescriptor = {
    key,
    label,
    kind: 'unknown',
    required,
    defaultValue: schema.default,
  };

  // anyOf (string | number) — the catalog's `value` / `delta.value` union.
  if (Array.isArray(schema.anyOf)) {
    const variants = schema.anyOf as JsonSchema[];
    const types = variants.map((v) => v.type);
    if (types.includes('string') && types.includes('number')) {
      return { ...base, kind: 'string-or-number' };
    }
    // Fall back to the first representable variant.
    const first = variants.find((v) => typeof v.type === 'string');
    if (first) return fieldFromJson(key, { ...first, default: schema.default }, required);
    return { ...base, kind: 'unknown' };
  }

  if (Array.isArray(schema.enum)) {
    return {
      ...base,
      kind: 'enum',
      enumValues: (schema.enum as unknown[]).map(String),
    };
  }

  switch (schema.type) {
    case 'string':
      return {
        ...base,
        kind: 'string',
        minLength: numberOrUndef(schema.minLength),
        maxLength: numberOrUndef(schema.maxLength),
      };
    case 'integer':
      return {
        ...base,
        kind: 'integer',
        minimum: numberOrUndef(schema.minimum),
        maximum: numberOrUndef(schema.maximum),
      };
    case 'number':
      return {
        ...base,
        kind: 'number',
        minimum: numberOrUndef(schema.minimum),
        maximum: numberOrUndef(schema.maximum),
      };
    case 'boolean':
      return { ...base, kind: 'boolean' };
    case 'object': {
      const childProps = (schema.properties ?? {}) as Record<string, JsonSchema>;
      const childReq = new Set(
        Array.isArray(schema.required) ? (schema.required as string[]) : [],
      );
      return {
        ...base,
        kind: 'object',
        fields: Object.keys(childProps).map((ck) =>
          fieldFromJson(ck, childProps[ck]!, childReq.has(ck)),
        ),
      };
    }
    case 'array': {
      // Tuple (prefixItems) vs homogeneous array (items).
      if (Array.isArray(schema.prefixItems)) {
        return {
          ...base,
          kind: 'tuple',
          minItems: numberOrUndef(schema.minItems),
          maxItems: numberOrUndef(schema.maxItems),
          prefixItems: (schema.prefixItems as JsonSchema[]).map((s, i) =>
            fieldFromJson(String(i), s, true),
          ),
        };
      }
      return {
        ...base,
        kind: 'array',
        minItems: numberOrUndef(schema.minItems),
        maxItems: numberOrUndef(schema.maxItems),
        item: schema.items
          ? fieldFromJson('item', schema.items as JsonSchema, false)
          : { key: 'item', label: 'Item', kind: 'string', required: false },
      };
    }
    default:
      return base;
  }
}

function numberOrUndef(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/* ─── Default value synthesis for a NEW item / node ─────────────────────── */

/** A blank value for one field descriptor (used when adding an array row). */
export function blankValueForField(field: FieldDescriptor): unknown {
  if (field.defaultValue !== undefined) return field.defaultValue;
  switch (field.kind) {
    case 'string':
    case 'binding':
    case 'action':
      return '';
    case 'number':
    case 'integer':
    case 'string-or-number':
      return field.minimum ?? 0;
    case 'boolean':
      return false;
    case 'enum':
      return field.enumValues?.[0] ?? '';
    case 'object':
      return Object.fromEntries(
        (field.fields ?? [])
          .filter((f) => f.required)
          .map((f) => [f.key, blankValueForField(f)]),
      );
    case 'tuple':
      return (field.prefixItems ?? []).map((f) => blankValueForField(f));
    case 'array':
      return [];
    default:
      return undefined;
  }
}

/**
 * Build the initial props bag for a freshly-inserted node. Prefers the
 * component's first golden fixture (a known-good, schema-valid prop bag), then
 * fills any REQUIRED field the fixture omitted from schema defaults / blanks so
 * the node parses immediately. Returns a deep clone (never the frozen fixture).
 */
export function defaultPropsFor(slug: string): Record<string, unknown> {
  const contract = getCockpitComponent(slug);
  if (!contract) return {};
  const fixture = contract.goldenFixtures[0];
  const base: Record<string, unknown> = fixture
    ? (structuredClone(fixture) as Record<string, unknown>)
    : {};
  for (const field of deriveFields(slug)) {
    if (
      field.required &&
      base[field.key] === undefined &&
      field.kind !== 'binding' &&
      field.kind !== 'action'
    ) {
      base[field.key] = blankValueForField(field);
    }
  }
  return base;
}

/* ─── Node id generation ────────────────────────────────────────────────── */

const NODE_ID_RE = /^[a-z][a-z0-9_-]{0,63}$/;

/** Collect every node id across the whole spec (root + pages). */
export function collectNodeIds(spec: CockpitSpec): Set<string> {
  const ids = new Set<string>();
  const walk = (n: CockpitNode): void => {
    ids.add(n.id);
    for (const c of n.children ?? []) walk(c);
  };
  walk(spec.root);
  for (const page of spec.pages ?? []) walk(page.root);
  return ids;
}

/**
 * Mint a globally-unique, schema-valid node id seeded from a component slug.
 * e.g. `MetricCard` → `metriccard`, `metriccard-2`, … Never collides with an
 * existing id anywhere in the spec.
 */
export function nextNodeId(slug: string, existing: Set<string>): string {
  const stem =
    slug
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '')
      .replace(/^[^a-z]+/, '') || 'node';
  if (NODE_ID_RE.test(stem) && !existing.has(stem)) return stem;
  for (let i = 2; i < 100000; i++) {
    const candidate = `${stem}-${i}`.slice(0, 64);
    if (NODE_ID_RE.test(candidate) && !existing.has(candidate)) return candidate;
  }
  // Pathological fallback (should never hit).
  return `node-${Date.now()}`.slice(0, 64);
}

/** Mint a unique page id seeded from a title. */
export function nextPageId(title: string, existing: Set<string>): string {
  const stem =
    title
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[^a-z]+/, '')
      .replace(/[-_]+$/, '')
      .slice(0, 60) || 'page';
  if (NODE_ID_RE.test(stem) && !existing.has(stem)) return stem;
  for (let i = 2; i < 100000; i++) {
    const candidate = `${stem}-${i}`.slice(0, 64);
    if (NODE_ID_RE.test(candidate) && !existing.has(candidate)) return candidate;
  }
  return `page-${Date.now()}`.slice(0, 64);
}

/* ─── Empty / starter spec ──────────────────────────────────────────────── */

/** A minimal valid CockpitSpec: an empty titled container, no children. */
export function emptyCockpitSpec(title = 'New Cockpit'): CockpitSpec {
  return {
    specVersion: COCKPIT_SPEC_VERSION,
    declaredBindingIds: [],
    declaredActionIds: [],
    root: {
      id: 'root',
      component: COCKPIT_ROOT_SLUG,
      props: { title },
      children: [],
    },
    meta: { title, authoredBy: 'human' },
  };
}

/* ─── Page addressing ───────────────────────────────────────────────────── */

/**
 * A page is addressed by index: `-1` = the implicit "main" page (`spec.root`),
 * `0..n` = `spec.pages[i]`. This mirrors `getCockpitPages` (main first) without
 * mutating the renderer contract. The editor tracks the active page by this id.
 */
export const MAIN_PAGE_INDEX = -1;

export interface EditorPageRef {
  /** -1 for the main page, else the `spec.pages` index. */
  index: number;
  id: string;
  title: string;
  icon?: string;
  root: CockpitNode;
}

/** The ordered, addressable page list (main first), for the page tabs. */
export function editorPages(spec: CockpitSpec): EditorPageRef[] {
  const main: EditorPageRef = {
    index: MAIN_PAGE_INDEX,
    id: 'main',
    title: spec.meta?.title?.trim() || spec.root.props?.title?.toString() || 'Main',
    root: spec.root,
  };
  const extras = (spec.pages ?? []).map<EditorPageRef>((p, i) => ({
    index: i,
    id: p.id,
    title: p.title,
    ...(p.icon ? { icon: p.icon } : {}),
    root: p.root,
  }));
  return [main, ...extras];
}

/** Resolve the page root tree for a page index (main = spec.root). */
export function pageRootAt(spec: CockpitSpec, pageIndex: number): CockpitNode {
  if (pageIndex === MAIN_PAGE_INDEX) return spec.root;
  return spec.pages?.[pageIndex]?.root ?? spec.root;
}

/* ─── Tree helpers ──────────────────────────────────────────────────────── */

export interface FlatTreeRow {
  node: CockpitNode;
  depth: number;
  /** Parent node id, or null for the page root. */
  parentId: string | null;
  /** Index within the parent's children array (root = 0). */
  childIndex: number;
}

/** Depth-first flatten of ONE page's tree, for the tree list view. */
export function flattenPageTree(root: CockpitNode): FlatTreeRow[] {
  const out: FlatTreeRow[] = [];
  const walk = (
    node: CockpitNode,
    depth: number,
    parentId: string | null,
    childIndex: number,
  ): void => {
    out.push({ node, depth, parentId, childIndex });
    const kids = node.children ?? [];
    for (let i = 0; i < kids.length; i++) walk(kids[i]!, depth + 1, node.id, i);
  };
  walk(root, 0, null, 0);
  return out;
}

/** Find a node (+ its parent) by id within one page tree. */
export function findNode(
  root: CockpitNode,
  id: string,
): { node: CockpitNode; parent: CockpitNode | null } | null {
  if (root.id === id) return { node: root, parent: null };
  const walk = (
    node: CockpitNode,
    parent: CockpitNode | null,
  ): { node: CockpitNode; parent: CockpitNode | null } | null => {
    if (node.id === id) return { node, parent };
    for (const c of node.children ?? []) {
      const found = walk(c, node);
      if (found) return found;
    }
    return null;
  };
  return walk(root, null);
}

/* ─── Immutable spec mutations ──────────────────────────────────────────── */

/** Replace a page's root tree in the spec (immutably). */
function withPageRoot(
  spec: CockpitSpec,
  pageIndex: number,
  newRoot: CockpitNode,
): CockpitSpec {
  if (pageIndex === MAIN_PAGE_INDEX) {
    return { ...spec, root: newRoot };
  }
  const pages = [...(spec.pages ?? [])];
  if (!pages[pageIndex]) return spec;
  pages[pageIndex] = { ...pages[pageIndex]!, root: newRoot };
  return { ...spec, pages };
}

/** Map a node tree, replacing the node with `id` via `fn` (immutable). */
function mapTree(
  node: CockpitNode,
  id: string,
  fn: (n: CockpitNode) => CockpitNode,
): CockpitNode {
  if (node.id === id) return fn(node);
  const kids = node.children;
  if (!kids || kids.length === 0) return node;
  let changed = false;
  const next = kids.map((c) => {
    const m = mapTree(c, id, fn);
    if (m !== c) changed = true;
    return m;
  });
  return changed ? { ...node, children: next } : node;
}

/** Remove the node with `id` from a tree (immutable). Never removes the root. */
function removeFromTree(node: CockpitNode, id: string): CockpitNode {
  const kids = node.children;
  if (!kids || kids.length === 0) return node;
  const filtered = kids.filter((c) => c.id !== id);
  let changed = filtered.length !== kids.length;
  const next = filtered.map((c) => {
    const m = removeFromTree(c, id);
    if (m !== c) changed = true;
    return m;
  });
  return changed ? { ...node, children: next } : node;
}

/**
 * Extract the binding-ref + action-ref values present in a node's prop bag, per
 * the component's slot contract (top-level slots + nested array paths). Used on
 * insert to AUTO-DECLARE the refs a golden fixture carries (e.g. ActionButton's
 * fixture references action `create_wo`) so the freshly-inserted node is valid
 * out of the box — no dead button, no spurious "undeclared ref" error.
 */
function collectRefsFromProps(
  slug: string,
  props: Record<string, unknown>,
): { bindings: string[]; actions: string[] } {
  const contract = getCockpitComponent(slug);
  const bindings = new Set<string>();
  const actions = new Set<string>();
  if (!contract) return { bindings: [], actions: [] };
  for (const s of contract.bindingSlots) {
    const v = props[s.prop];
    if (typeof v === 'string' && v.length > 0) bindings.add(v);
  }
  for (const s of contract.actionSlots) {
    const v = props[s.prop];
    if (typeof v === 'string' && v.length > 0) actions.add(v);
  }
  // Nested array refs (e.g. ButtonGroup.buttons[].action). The field is optional
  // on older @argusai/shared type-builds — read it defensively.
  const nestedAction =
    (contract as { nestedActionPaths?: string[] }).nestedActionPaths ?? [];
  const nestedBinding =
    (contract as { nestedBindingPaths?: string[] }).nestedBindingPaths ?? [];
  for (const path of nestedAction) extractNestedRefs(props, path).forEach((r) => actions.add(r));
  for (const path of nestedBinding) extractNestedRefs(props, path).forEach((r) => bindings.add(r));
  return { bindings: [...bindings], actions: [...actions] };
}

/** Pull every ref value at an `<arrayProp>[].<field>` path on a prop bag. */
function extractNestedRefs(
  props: Record<string, unknown>,
  nestedPath: string,
): string[] {
  const m = /^([a-zA-Z0-9_]+)\[\]\.([a-zA-Z0-9_]+)$/.exec(nestedPath);
  if (!m) return [];
  const [, arrayProp, refField] = m;
  const arr = props[arrayProp!];
  if (!Array.isArray(arr)) return [];
  const out: string[] = [];
  for (const entry of arr) {
    if (entry && typeof entry === 'object') {
      const v = (entry as Record<string, unknown>)[refField!];
      if (typeof v === 'string' && v.length > 0) out.push(v);
    }
  }
  return out;
}

export interface InsertNodeArgs {
  spec: CockpitSpec;
  pageIndex: number;
  /** Parent node id to insert under; falls back to the page root if not found. */
  parentId: string | null;
  slug: string;
}

/**
 * Insert a new node (seeded from defaults) under `parentId` in `pageIndex`.
 * Auto-declares any binding/action refs the seed props carry so the node is
 * immediately valid. Returns the new spec + the new node's id (so the editor
 * can select it).
 */
export function insertNode(args: InsertNodeArgs): {
  spec: CockpitSpec;
  nodeId: string;
} {
  const { spec, pageIndex, slug } = args;
  const existingIds = collectNodeIds(spec);
  const nodeId = nextNodeId(slug, existingIds);
  const seedProps = defaultPropsFor(slug);
  const newNode: CockpitNode = {
    id: nodeId,
    component: slug,
    props: seedProps,
  };
  const root = pageRootAt(spec, pageIndex);
  const targetParentId =
    args.parentId && findNode(root, args.parentId) ? args.parentId : root.id;
  const newRoot = mapTree(root, targetParentId, (parent) => ({
    ...parent,
    children: [...(parent.children ?? []), newNode],
  }));
  // Auto-declare the refs the seeded props reference so the node is valid.
  const { bindings, actions } = collectRefsFromProps(slug, seedProps);
  let nextSpec = withPageRoot(spec, pageIndex, newRoot);
  for (const b of bindings) nextSpec = addBindingId(nextSpec, b);
  for (const a of actions) nextSpec = addActionId(nextSpec, a);
  return { spec: nextSpec, nodeId };
}

/** Replace a node's props bag (immutable). */
export function updateNodeProps(
  spec: CockpitSpec,
  pageIndex: number,
  nodeId: string,
  props: Record<string, unknown>,
): CockpitSpec {
  const root = pageRootAt(spec, pageIndex);
  const newRoot = mapTree(root, nodeId, (n) => ({ ...n, props }));
  return withPageRoot(spec, pageIndex, newRoot);
}

/** Delete a node (and its subtree). The page root cannot be deleted. */
export function deleteNode(
  spec: CockpitSpec,
  pageIndex: number,
  nodeId: string,
): CockpitSpec {
  const root = pageRootAt(spec, pageIndex);
  if (root.id === nodeId) return spec; // never delete a page root
  const newRoot = removeFromTree(root, nodeId);
  return withPageRoot(spec, pageIndex, newRoot);
}

/** Move a node up/down among its siblings (immutable). */
export function moveNode(
  spec: CockpitSpec,
  pageIndex: number,
  nodeId: string,
  direction: 'up' | 'down',
): CockpitSpec {
  const root = pageRootAt(spec, pageIndex);
  const located = findNode(root, nodeId);
  if (!located || !located.parent) return spec;
  const parent = located.parent;
  const kids = parent.children ?? [];
  const idx = kids.findIndex((c) => c.id === nodeId);
  if (idx < 0) return spec;
  const swapWith = direction === 'up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= kids.length) return spec;
  const next = [...kids];
  [next[idx], next[swapWith]] = [next[swapWith]!, next[idx]!];
  const newRoot = mapTree(root, parent.id, (p) => ({ ...p, children: next }));
  return withPageRoot(spec, pageIndex, newRoot);
}

/* ─── Page mutations ────────────────────────────────────────────────────── */

/** True when another page can be added (respects the schema cap). */
export function canAddPage(spec: CockpitSpec): boolean {
  return (spec.pages?.length ?? 0) < COCKPIT_SPEC_MAX_PAGES;
}

/** Add a new (empty) page. Returns the new spec + the new page id. */
export function addPage(
  spec: CockpitSpec,
  title = 'New page',
): { spec: CockpitSpec; pageId: string } {
  if (!canAddPage(spec)) return { spec, pageId: '' };
  const existingNodeIds = collectNodeIds(spec);
  const existingPageIds = new Set((spec.pages ?? []).map((p) => p.id));
  const pageId = nextPageId(title, existingPageIds);
  const rootId = nextNodeId('page_root', existingNodeIds);
  const page: CockpitPage = {
    id: pageId,
    title,
    root: {
      id: rootId,
      component: COCKPIT_ROOT_SLUG,
      props: { title },
      children: [],
    },
  };
  return { spec: { ...spec, pages: [...(spec.pages ?? []), page] }, pageId };
}

/** Rename a page (the main page renames `meta.title` + the root's title). */
export function renamePage(
  spec: CockpitSpec,
  pageIndex: number,
  title: string,
): CockpitSpec {
  const clean = title.trim().slice(0, 80) || 'Untitled';
  if (pageIndex === MAIN_PAGE_INDEX) {
    return {
      ...spec,
      meta: { ...(spec.meta ?? { authoredBy: 'human' as const }), title: clean },
      root: {
        ...spec.root,
        props: { ...(spec.root.props ?? {}), title: clean },
      },
    };
  }
  const pages = [...(spec.pages ?? [])];
  if (!pages[pageIndex]) return spec;
  pages[pageIndex] = { ...pages[pageIndex]!, title: clean };
  return { ...spec, pages };
}

/** Delete an extra page (the main page cannot be deleted). */
export function deletePage(spec: CockpitSpec, pageIndex: number): CockpitSpec {
  if (pageIndex === MAIN_PAGE_INDEX) return spec;
  const pages = (spec.pages ?? []).filter((_, i) => i !== pageIndex);
  return { ...spec, pages: pages.length > 0 ? pages : undefined };
}

/** Reorder an extra page among the extra pages (main is always first). */
export function reorderPage(
  spec: CockpitSpec,
  pageIndex: number,
  direction: 'left' | 'right',
): CockpitSpec {
  if (pageIndex === MAIN_PAGE_INDEX) return spec;
  const pages = [...(spec.pages ?? [])];
  const swapWith = direction === 'left' ? pageIndex - 1 : pageIndex + 1;
  if (swapWith < 0 || swapWith >= pages.length) return spec;
  [pages[pageIndex], pages[swapWith]] = [pages[swapWith]!, pages[pageIndex]!];
  return { ...spec, pages };
}

/* ─── Declared binding / action id management ───────────────────────────── */

const REF_ID_RE = /^[A-Za-z0-9._:-]{1,120}$/;

/** True when a candidate binding/action id is well-formed. */
export function isValidRefId(id: string): boolean {
  return REF_ID_RE.test(id);
}

/** Add a declared binding id (dedup, validated). */
export function addBindingId(spec: CockpitSpec, id: string): CockpitSpec {
  const clean = id.trim();
  if (!isValidRefId(clean) || spec.declaredBindingIds.includes(clean)) return spec;
  return { ...spec, declaredBindingIds: [...spec.declaredBindingIds, clean] };
}

/** Add a declared action id (dedup, validated). */
export function addActionId(spec: CockpitSpec, id: string): CockpitSpec {
  const clean = id.trim();
  if (!isValidRefId(clean) || spec.declaredActionIds.includes(clean)) return spec;
  return { ...spec, declaredActionIds: [...spec.declaredActionIds, clean] };
}

/** Remove a declared binding id. */
export function removeBindingId(spec: CockpitSpec, id: string): CockpitSpec {
  return {
    ...spec,
    declaredBindingIds: spec.declaredBindingIds.filter((b) => b !== id),
  };
}

/** Remove a declared action id. */
export function removeActionId(spec: CockpitSpec, id: string): CockpitSpec {
  return {
    ...spec,
    declaredActionIds: spec.declaredActionIds.filter((a) => a !== id),
  };
}

/* ─── Spec detection (raw-HTML vs CockpitSpec cockpit) ──────────────────── */

/**
 * Pull a persisted CockpitSpec out of a version's `sandboxConfig`, if present.
 * Returns the raw value (unvalidated) — the editor runs `parseCockpitSpec` on
 * it before trusting it. A version with no `cockpitSpec` is a legacy raw-HTML
 * cockpit and stays on the Monaco editor path.
 */
export function readCockpitSpecFromSandbox(
  sandboxConfig: Record<string, unknown> | null | undefined,
): unknown {
  if (!sandboxConfig || typeof sandboxConfig !== 'object') return undefined;
  return (sandboxConfig as { cockpitSpec?: unknown }).cockpitSpec;
}
